import type { FC } from 'react';
import { Avatar, Empty, Flex, Space, Spin, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TicketVisitReportDto } from '@evimo/client/models';
import {
  WORK_ORDER_UI,
  executionResultTone,
  softBadgeStyle,
} from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import {
  TicketReportSnippet,
  ticketHasReport,
} from '~/app/components/maintenance/work-orders/WorkOrderKanbanPageContainer/components/TicketReportSnippet/TicketReportSnippet';
import { useTicketDetailVisits } from './hooks/useTicketDetailVisits';
import { formatVisitCompletedAt } from './utils';

export interface TicketDetailVisitsProps {
  visits: TicketVisitReportDto[] | undefined;
  loading?: boolean;
}

export const TicketDetailVisits: FC<TicketDetailVisitsProps> = ({
  visits,
  loading = false,
}) => {
  const { t, i18n } = useTranslation('maintenance');
  const visitViews = useTicketDetailVisits(visits);

  return (
    <Spin spinning={loading}>
      {visitViews.length === 0 && !loading ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('workOrders.ticketDetail.visits.empty')}
        />
      ) : (
        <>
          <Typography.Text
            type="secondary"
            style={{ display: 'block', marginBottom: 12, fontSize: 12 }}
          >
            {t('workOrders.ticketDetail.visits.hint')}
          </Typography.Text>
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {visitViews.map((visit) => {
              const resultLabel = visit.executionResult
                ? t(`workOrders.executionResult.${visit.executionResult}`, {
                    defaultValue: visit.executionResult,
                  })
                : null;
              const when = formatVisitCompletedAt(
                visit.completedAt,
                i18n.language,
              );
              const completer =
                visit.completedByName ??
                (visit.completedById
                  ? t('workOrders.ticketDetail.visits.unknownActor')
                  : null);

              return (
                <div
                  key={visit.membershipId}
                  style={{
                    padding: 12,
                    borderRadius: 10,
                    border: `1px solid ${WORK_ORDER_UI.cardBorder}`,
                    background: '#fff',
                  }}
                >
                  <Flex align="center" gap={8} wrap="wrap">
                    <Typography.Text strong style={{ color: '#0f172a' }}>
                      {t('workOrders.ticketDetail.visits.visitTitle', {
                        number: visit.executionNumber,
                        code: visit.workOrderCode,
                      })}
                    </Typography.Text>
                    {resultLabel && visit.executionResult ? (
                      <Tag
                        style={softBadgeStyle(
                          executionResultTone(visit.executionResult),
                        )}
                      >
                        {resultLabel}
                      </Tag>
                    ) : null}
                    {visit.membershipStatus !== 'ACTIVE' ? (
                      <Tag>
                        {t(
                          `workOrders.ticketDetail.visits.membershipStatus.${visit.membershipStatus}`,
                          { defaultValue: visit.membershipStatus },
                        )}
                      </Tag>
                    ) : null}
                  </Flex>

                  <Flex
                    align="center"
                    gap={8}
                    wrap="wrap"
                    style={{ marginTop: 6 }}
                  >
                    {when ? (
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                      >
                        {t('workOrders.ticketDetail.visits.completedAt', {
                          date: when,
                        })}
                      </Typography.Text>
                    ) : (
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                      >
                        {t('workOrders.ticketDetail.visits.notCompleted')}
                      </Typography.Text>
                    )}
                    {completer ? (
                      <Space size={6} align="center">
                        <Avatar
                          size={20}
                          style={{
                            background: WORK_ORDER_UI.sidebarNavy,
                            fontSize: 11,
                          }}
                        >
                          {completer.slice(0, 1).toUpperCase()}
                        </Avatar>
                        <Typography.Text style={{ fontSize: 12 }}>
                          {completer}
                        </Typography.Text>
                      </Space>
                    ) : null}
                  </Flex>

                  {ticketHasReport(visit.report) ? (
                    <TicketReportSnippet
                      report={visit.report}
                      defaultExpanded
                    />
                  ) : visit.executionResult ? (
                    <Typography.Text
                      type="secondary"
                      style={{ display: 'block', marginTop: 8, fontSize: 12 }}
                    >
                      {t('workOrders.ticketDetail.visits.noReportBody')}
                    </Typography.Text>
                  ) : null}
                </div>
              );
            })}
          </Space>
        </>
      )}
    </Spin>
  );
};
